<template>
  <div
    v-if="conference"
    class="conf-notice-overlay"
    @click.self="front.closeConferenceNotice"
  >
    <div class="conf-notice-modal" role="dialog" aria-modal="true" aria-labelledby="conf-notice-title">
      <header class="conf-notice-modal__header">
        <div>
          <span class="conf-notice-kicker">未盖章通知预览</span>
          <h3 id="conf-notice-title">{{ conference.conferenceTitle }}</h3>
        </div>
        <button type="button" class="conf-notice-close" aria-label="关闭" @click="front.closeConferenceNotice">×</button>
      </header>

      <div class="conf-notice-modal__body">
        <div v-if="previewLoading" class="conf-notice-banner">正在加载会议通知…</div>
        <div v-else-if="previewError" class="conf-notice-banner is-error">{{ previewError }}</div>
        <iframe
          v-else-if="previewSrc"
          :src="previewSrc"
          title="会议通知"
          class="conf-notice-iframe"
        />
        <template v-else>
          <div class="conf-notice-banner">
            本通知未加盖学会电子章，仅供查阅。缴费终审确认后可下载盖章版 PDF。
          </div>
          <p><strong>主办：</strong>{{ front.associationNameById(conference.associationId) }}</p>
          <p><strong>时间：</strong>{{ front.formatDateRange(conference.startDate, conference.endDate) }}</p>
          <p><strong>地点：</strong>{{ conference.city || '地点待定' }}</p>
          <p><strong>会议简介：</strong>{{ noticeDesc }}</p>

          <div class="conf-fee-box">
            <strong>四类注册费标准</strong>
            <div
              v-for="row in feeRows"
              :key="row.key"
              class="conf-fee-row"
              :class="{ 'is-mine': row.isUserType }"
            >
              <span>
                {{ row.label }}
                <em v-if="row.isUserType">您的类型</em>
              </span>
              <span>{{ row.amount > 0 ? `¥${row.amount}` : '--' }}</span>
            </div>
          </div>

          <p class="conf-notice-deadlines">
            缴费截止：{{ front.formatDate(conference.paymentDeadline) || '待定' }}
            · 摘要截止：{{ front.formatDate(conference.abstractDeadline) || '待定' }}
          </p>
        </template>
      </div>

      <footer class="conf-notice-modal__footer">
        <button type="button" class="ghost-action compact" @click="front.closeConferenceNotice">关闭</button>
        <button
          v-if="registrationOpen"
          type="button"
          class="primary-action compact"
          @click="front.goRegisterFromNotice(conference)"
        >
          前往报名
        </button>
        <button
          v-else
          type="button"
          class="primary-action compact"
          disabled
        >
          {{ front.conferenceListStatusLabel(conference) }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConferenceNoticeModal',
  inject: ['front'],
  data() {
    return {
      previewSrc: '',
      previewLoading: false,
      previewError: '',
      previewObjectUrl: ''
    }
  },
  computed: {
    conference() {
      return this.front.noticePreviewConference
    },
    noticeFileUrl() {
      if (!this.conference) {
        return ''
      }
      const fromMaterials = this.front.selectedMaterials && this.front.selectedMaterials.publicNoticeUrl
      return fromMaterials || this.conference.publicNoticeUrl || ''
    },
    feeRows() {
      return this.front.conferenceFeeRows(this.conference)
    },
    noticeDesc() {
      if (!this.conference) {
        return ''
      }
      return this.conference.description
        || this.conference.intro
        || '欢迎参会代表完成会议注册费缴纳后填报参会信息。'
    },
    registrationOpen() {
      return this.front.isConferenceRegistrationOpen(this.conference)
    }
  },
  watch: {
    noticeFileUrl: {
      immediate: true,
      handler(url) {
        this.loadPreview(url)
      }
    }
  },
  beforeDestroy() {
    this.revokePreview()
  },
  methods: {
    revokePreview() {
      if (this.previewObjectUrl) {
        try {
          URL.revokeObjectURL(this.previewObjectUrl)
        } catch (e) {
          // ignore
        }
        this.previewObjectUrl = ''
      }
      this.previewSrc = ''
    },
    async loadPreview(url) {
      this.revokePreview()
      this.previewError = ''
      if (!url) {
        this.previewLoading = false
        return
      }
      this.previewLoading = true
      try {
        const src = await this.front.resolvePreviewObjectUrl(url)
        if (src && String(src).startsWith('blob:')) {
          this.previewObjectUrl = src
        }
        this.previewSrc = src || ''
        if (!this.previewSrc) {
          this.previewError = '暂无法预览会议通知文件'
        }
      } catch (error) {
        this.previewError = (error && error.message) || '会议通知加载失败'
      } finally {
        this.previewLoading = false
      }
    }
  }
}
</script>

<style scoped>
.conf-notice-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.conf-notice-modal {
  width: min(640px, 100%);
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
}

.conf-notice-modal__header {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border);
  background: #fff;
}

.conf-notice-kicker {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.conf-notice-modal__header h3 {
  margin: 4px 0 0;
  color: var(--primary);
  font-size: 16px;
}

.conf-notice-close {
  border: 0;
  background: transparent;
  color: #94a3b8;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.conf-notice-modal__body {
  padding: 18px 20px;
  display: grid;
  gap: 12px;
  font-size: 13px;
  color: var(--subtle);
  line-height: 1.7;
}

.conf-notice-modal__body p {
  margin: 0;
}

.conf-notice-iframe {
  width: 100%;
  height: 360px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
}

.conf-notice-banner {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  font-size: 12px;
}

.conf-notice-banner.is-error {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.conf-fee-box {
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
  display: grid;
  gap: 6px;
}

.conf-fee-box > strong {
  color: var(--primary);
  font-size: 13px;
  margin-bottom: 2px;
}

.conf-fee-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid var(--border);
  font-size: 12px;
}

.conf-fee-row.is-mine {
  background: #f0fdf4;
  border-color: #86efac;
  font-weight: 700;
  color: var(--primary);
}

.conf-fee-row em {
  margin-left: 6px;
  font-style: normal;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
}

.conf-notice-deadlines {
  color: #64748b;
  font-size: 12px;
}

.conf-notice-modal__footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 16px;
  border-top: 1px solid var(--border);
  background: #f8fafc;
}
</style>
